/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import * as THREE from 'three';
import type * as MatSDKTypes from '../WasmModules/MatSDK';
import MatSDK from '../WasmModules/MatSDK';
import type { IEmLifetime } from '../WasmModules/MatSDK/Types/Interfaces';

// Lifetime management for MatSDK types
export function emmanaged<T extends IEmLifetime, U>(resource: T, func: (res: T) => U): U {
  try {
    return func(resource);
  } finally {
    resource.delete();
  }
}

// Lifetime management for MatSDK types
export function emmanaged2<T1 extends IEmLifetime, T2 extends IEmLifetime, U>(
  resource1: T1,
  resource2: T2,
  func: (res1: T1, res2: T2) => U
): U {
  try {
    return func(resource1, resource2);
  } finally {
    // destructing in reverse order
    resource2.delete();
    resource1.delete();
  }
}

// Lifetime management for MatSDK types
export function emmanaged3<T1 extends IEmLifetime, T2 extends IEmLifetime, T3 extends IEmLifetime, U>(
  resource1: T1,
  resource2: T2,
  resource3: T3,
  func: (res1: T1, res2: T2, res3: T3) => U
): U {
  try {
    return func(resource1, resource2, resource3);
  } finally {
    // destructing in reverse order
    resource3.delete();
    resource2.delete();
    resource1.delete();
  }
}

// Lifetime management for MatSDK types
export function emmanaged4<
  T1 extends IEmLifetime,
  T2 extends IEmLifetime,
  T3 extends IEmLifetime,
  T4 extends IEmLifetime,
  U
>(resource1: T1, resource2: T2, resource3: T3, resource4: T4, func: (res1: T1, res2: T2, res3: T3, res4: T4) => U): U {
  try {
    return func(resource1, resource2, resource3, resource4);
  } finally {
    // destructing in reverse order
    resource4.delete();
    resource3.delete();
    resource2.delete();
    resource1.delete();
  }
}

// Lifetime management for MatSDK types
export function emmanaged5<
  T1 extends IEmLifetime,
  T2 extends IEmLifetime,
  T3 extends IEmLifetime,
  T4 extends IEmLifetime,
  T5 extends IEmLifetime,
  U
>(
  resource1: T1,
  resource2: T2,
  resource3: T3,
  resource4: T4,
  resource5: T5,
  func: (res1: T1, res2: T2, res3: T3, res4: T4, res5: T5) => U
): U {
  try {
    return func(resource1, resource2, resource3, resource4, resource5);
  } finally {
    // destructing in reverse order
    resource5.delete();
    resource4.delete();
    resource3.delete();
    resource2.delete();
    resource1.delete();
  }
}

export function createBufferGeometry(
  triangleSurface: MatSDKTypes.TriangleSurface,
  colors?: Float32Array
): THREE.BufferGeometry {
  const vertices = triangleSurface.getRawVertices();
  const triangles = triangleSurface.getRawTriangles();
  const coordinates = new Float32Array(vertices.length);
  const indices = new (vertices.length / 3 > 65535 ? Uint32Array : Uint16Array)(triangles.length);
  for (let i = 0; i < vertices.length; ++i) {
    coordinates[i] = vertices[i];
  }
  for (let i = 0; i < triangles.length; ++i) {
    indices[i] = triangles[i];
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(coordinates, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  if (colors && colors.length > 0) {
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }
  geometry.computeVertexNormals();
  return geometry;
}

export function createBufferGeometryFromPolyline(polyline: MatSDKTypes.Polyline3d): THREE.BufferGeometry {
  const points = polyline.getRawPoints();
  const coordinates = new Float32Array(polyline.closed ? points.length + 3 : points.length);
  for (let i = 0, cnt = points.length; i < cnt; ++i) {
    coordinates[i] = points[i];
  }
  if (polyline.closed) {
    coordinates[points.length] = points[0];
    coordinates[points.length + 1] = points[1];
    coordinates[points.length + 2] = points[2];
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(coordinates, 3));
  return geometry;
}

export function createBufferGeometryFromLineSegments(segments: MatSDKTypes.LineSegments): THREE.BufferGeometry {
  const vertices = segments.getRawVertices();
  const pairs = segments.getRawSegments();
  const coordinates = new Float32Array(pairs.length * 3);
  for (let i = 0; i < pairs.length; ++i) {
    const j = i * 3;
    const k = pairs[i] * 3;
    coordinates[j] = vertices[k];
    coordinates[j + 1] = vertices[k + 1];
    coordinates[j + 2] = vertices[k + 2];
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(coordinates, 3));
  return geometry;
}

export function createBufferGeometryFromPolylinesVector(polylines: MatSDKTypes.VectorPolyline3d): THREE.BufferGeometry {
  const vertices: Array<number> = [];
  const pairs: Array<number> = [];
  let l = 0;
  for (let i = 0, sz = polylines.size(); i < sz; ++i) {
    const polyline3d = polylines.access(i);
    const pts3d = polyline3d.accessPoints();
    const cnt = pts3d.size();
    for (let j = 0; j < cnt; ++j) {
      const pt3d = pts3d.access(j).toArray();
      vertices.push(pt3d[0]);
      vertices.push(pt3d[1]);
      vertices.push(pt3d[2]);
      if (j > 0) {
        pairs.push(l - 1);
        pairs.push(l);
      }
      l++;
    }
  }
  const coordinates = new Float32Array(pairs.length * 3);
  for (let i = 0; i < pairs.length; ++i) {
    const j = i * 3;
    const k = pairs[i] * 3;
    coordinates[j] = vertices[k];
    coordinates[j + 1] = vertices[k + 1];
    coordinates[j + 2] = vertices[k + 2];
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(coordinates, 3));
  return geometry;
}

export function createTriangleSurface(
  bufferGeometry: THREE.BufferGeometry,
  fixTopology = false
): MatSDKTypes.TriangleSurface {
  const ret = new MatSDK.TriangleSurface();
  if (bufferGeometry.index) {
    ret.setFromRaw(bufferGeometry.attributes.position.array, bufferGeometry.index.array);
    if (fixTopology) {
      ret.fixTopology();
    }
  }
  return ret;
}
